<template>
  <div class="org-users">
    <el-card>
      <div slot="header" class="card-header">
        <span>推广用户列表</span>
        <el-input
          v-model="keyword"
          placeholder="搜索用户名/姓名/手机号"
          style="width: 250px"
          clearable
          @clear="loadUsers"
          @keyup.enter.native="loadUsers"
        >
          <el-button slot="append" icon="el-icon-search" @click="loadUsers"></el-button>
        </el-input>
      </div>

      <el-table :data="userList" v-loading="loading" stripe>
        <el-table-column prop="id" label="用户ID" width="80"></el-table-column>
        <el-table-column prop="userName" label="用户名" width="150"></el-table-column>
        <el-table-column prop="realName" label="真实姓名" width="120"></el-table-column>
        <el-table-column prop="phone" label="手机号" width="130"></el-table-column>
        <el-table-column prop="email" label="邮箱" width="180"></el-table-column>
        <el-table-column prop="promoCode" label="使用的推广码" width="120"></el-table-column>
        <el-table-column prop="bindTime" label="绑定时间" width="180">
          <template slot-scope="scope">
            {{ formatTime(scope.row.bindTime) }}
          </template>
        </el-table-column>
        <el-table-column prop="createTime" label="注册时间" width="180">
          <template slot-scope="scope">
            {{ formatTime(scope.row.createTime) }}
          </template>
        </el-table-column>
        <el-table-column label="操作" width="180" fixed="right">
          <template slot-scope="scope">
            <el-button type="text" size="small" @click="viewOrders(scope.row)">
              查看购买记录
            </el-button>
            <el-button type="text" size="small" @click="openStudyInfo(scope.row)">
              查看学习信息
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <el-dialog
        :visible.sync="studyDialogVisible"
        width="520px"
        :close-on-click-modal="false"
        :title="studyDialogTitle"
      >
        <div v-loading="studyLoading" class="study-dialog-body">
          <div v-if="studyEmpty" class="study-empty">
            暂无学习计划
          </div>
          <div v-else class="study-metrics">
            <div class="metric-item">
              <div class="metric-value">{{ studyMetrics.accuracy }}%</div>
              <div class="metric-label">总正确率</div>
            </div>
            <div class="metric-item">
              <div class="metric-value">{{ studyMetrics.progress }}%</div>
              <div class="metric-label">备考进度</div>
            </div>
            <div class="metric-item">
              <div class="metric-value" :style="{ color: studyMetrics.unreviewedWrongCount > 0 ? '#ff4d4f' : '#52c41a' }">
                {{ studyMetrics.unreviewedWrongCount }}
              </div>
              <div class="metric-label">待复习错题</div>
            </div>
          </div>
          <div v-if="studyLevelLabel" class="study-level">
            当前级别：{{ studyLevelLabel }}
          </div>
        </div>
        <span slot="footer">
          <el-button @click="studyDialogVisible = false">关闭</el-button>
        </span>
      </el-dialog>

      <el-pagination
        class="pagination"
        background
        layout="total, sizes, prev, pager, next, jumper"
        :total="total"
        :page-size="pageSize"
        :page-sizes="[10, 20, 50, 100]"
        :current-page="pageNum"
        @size-change="handleSizeChange"
        @current-change="handlePageChange"
      ></el-pagination>
    </el-card>
  </div>
</template>

<script>
import { getOrgUsers } from '@/api/organization'
import { countByRepoId, getAccuracyByRepoId, unreviewedWrongCount } from '@/api/userQuestionRecord'
import { listAmcStudySchemesByUser } from '@/api/amcStudyScheme'

export default {
  name: 'OrgUsers',
  data() {
    return {
      userList: [],
      loading: false,
      keyword: '',
      pageNum: 1,
      pageSize: 10,
      total: 0,

      studyDialogVisible: false,
      studyDialogTitle: '学习信息',
      studyLoading: false,
      studyEmpty: false,
      studyLevelLabel: '',
      studyMetrics: {
        accuracy: 0,
        progress: 0,
        unreviewedWrongCount: 0
      }
    }
  },
  created() {
    this.loadUsers()
  },
  methods: {
    async loadUsers() {
      this.loading = true
      try {
        const res = await getOrgUsers({
          pageNum: this.pageNum,
          pageSize: this.pageSize,
          keyword: this.keyword
        })
        if (res.code === 1) {
          this.userList = res.data.records || []
          this.total = res.data.total || 0
        }
      } catch (e) {
        console.error('加载用户列表失败:', e)
      } finally {
        this.loading = false
      }
    },
    handleSizeChange(size) {
      this.pageSize = size
      this.pageNum = 1
      this.loadUsers()
    },
    handlePageChange(page) {
      this.pageNum = page
      this.loadUsers()
    },
    viewOrders(user) {
      this.$router.push({
        path: '/org/orders',
        query: { userId: user.id, userName: user.userName }
      })
    },
    levelLabelByRepoId(repoId) {
      const map = { 14: 'AMC8', 15: 'AMC10', 16: 'AMC12' }
      return map[Number(repoId)] || ''
    },
    async getSelectedRepoId(userId) {
      // getAmcInfoByUserId 已废弃，AMC信息表不再使用
      // 暂时返回 null，如需获取用户选择的题库，请使用学习计划相关接口
      // const res = await getAmcInfoByUserId(userId)
      // const list = Array.isArray(res?.data) ? res.data : (Array.isArray(res?.data?.data) ? res.data.data : [])
      // const selected = list.find(x => Number(x.amcMark) === 1)
      // return selected ? Number(selected.amcRepo) : null
      return null
    },
    async getPlanTotalQuestions(userId, repoId) {
      const resp = await listAmcStudySchemesByUser(userId)
      const raw = resp?.data?.data ?? resp?.data ?? []
      const arr = Array.isArray(raw) ? raw : []
      const matched = arr.find(s => Number(s.amcRepo ?? s.repoId) === Number(repoId))
      const questionIdStr = matched && matched.questionId
      if (!questionIdStr) return 0
      return String(questionIdStr)
        .split(',')
        .map(x => x.trim())
        .filter(Boolean).length
    },
    async openStudyInfo(user) {
      if (!user || !user.id) return
      this.studyDialogVisible = true
      this.studyDialogTitle = `学习信息 - ${user.realName || user.userName || user.id}`
      this.studyLoading = true
      this.studyEmpty = false
      this.studyLevelLabel = ''
      this.studyMetrics = { accuracy: 0, progress: 0, unreviewedWrongCount: 0 }
      try {
        const repoId = await this.getSelectedRepoId(user.id)
        if (!repoId) {
          this.studyEmpty = true
          return
        }

        this.studyLevelLabel = this.levelLabelByRepoId(repoId)

        const [doneRes, accRes, wrongRes, planTotal] = await Promise.all([
          countByRepoId(repoId, user.id),
          getAccuracyByRepoId(repoId, user.id),
          unreviewedWrongCount(repoId, user.id),
          this.getPlanTotalQuestions(user.id, repoId)
        ])

        const completed = typeof doneRes?.data === 'number' ? doneRes.data : Number(doneRes?.data?.data || 0) || 0
        const ratio = typeof accRes?.data === 'number' ? accRes.data : Number(accRes?.data?.data || 0) || 0
        const wrongCnt = typeof wrongRes?.data === 'number' ? wrongRes.data : Number(wrongRes?.data?.data || 0) || 0

        const total = Number(planTotal) || 0
        const progress = total > 0 ? Math.round((completed / total) * 100) : 0

        this.studyMetrics = {
          accuracy: Math.round(Math.max(0, Math.min(1, ratio)) * 100),
          progress: Math.min(100, Math.max(0, progress)),
          unreviewedWrongCount: wrongCnt
        }
      } catch (e) {
        console.error('加载学习信息失败:', e)
        this.studyEmpty = true
      } finally {
        this.studyLoading = false
      }
    },
    formatTime(time) {
      if (!time) return '-'
      if (typeof time === 'string') return time.replace('T', ' ').substring(0, 19)
      return time
    }
  }
}
</script>

<style scoped>
.org-users {
  padding: 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.pagination {
  margin-top: 20px;
  text-align: right;
}

.study-dialog-body {
  min-height: 120px;
}

.study-empty {
  padding: 24px 0;
  text-align: center;
  color: #909399;
}

.study-metrics {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  padding-top: 8px;
}

.metric-item {
  background: #f5f7fa;
  border-radius: 8px;
  padding: 16px 12px;
  text-align: center;
}

.metric-value {
  font-size: 22px;
  font-weight: 700;
  color: #303133;
  line-height: 1.2;
}

.metric-label {
  margin-top: 6px;
  font-size: 12px;
  color: #909399;
}

.study-level {
  margin-top: 12px;
  text-align: center;
  color: #606266;
  font-size: 13px;
}
</style>
